import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Satellite, CloudSun, Sprout as SproutIcon, ShieldCheck, Phone, ArrowRight, Check, Loader2, ExternalLink, Locate, RefreshCw, Mic, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Brand, EarthGlobe } from "@/components/bits";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";

const CROPS = ["cotton", "soybean", "wheat", "paddy", "sugarcane", "tomato", "chilli"];
const STATE_LANGUAGE = {
  Maharashtra: "Marathi (Deshi)", Punjab: "Punjabi (Malwai)", "Uttar Pradesh": "Hindi (Awadhi)", Karnataka: "Kannada (North Karnataka)",
  Telangana: "Telugu (Telangana)", Gujarat: "Gujarati (Kathiawadi)", "Tamil Nadu": "Tamil (Kongu)", "Madhya Pradesh": "Hindi (Malwi)",
};
const PIPELINE = [
  { key: "locate", label: "Resolving farm location", icon: Locate },
  { key: "field_boundaries", label: "Detecting field boundary", icon: MapPin },
  { key: "satellite_indices", label: "Reading Sentinel-2 indices", icon: Satellite },
  { key: "soil", label: "Pulling soil + weather facts", icon: CloudSun },
  { key: "ndvi_history", label: "Checking NDVI history", icon: RefreshCw },
  { key: "sentinel2_scene", label: "Fetching satellite image", icon: Satellite },
  { key: "inference", label: "Inferring crop & growth stage", icon: SproutIcon },
];

function useMap(containerRef, markerRef, polygonRef, onChange) {
  const mapRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: false, attributionControl: true }).setView([21.15, 78.83], 13);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map);
    const icon = L.divIcon({
      className: "",
      html: '<div class="pin-outer"><div class="pin-core"></div></div>',
      iconSize: [26, 26], iconAnchor: [13, 13],
    });
    const marker = L.marker([21.15, 78.83], { draggable: true, icon }).addTo(map);
    marker.on("dragend", () => { const p = marker.getLatLng(); onChange(p.lat, p.lng); });
    map.on("click", (e) => { marker.setLatLng(e.latlng); onChange(e.latlng.lat, e.latlng.lng); });
    mapRef.current = map; markerRef.current = marker;
  }, [containerRef, markerRef, onChange]);
  return { mapRef, drawPolygon: (points) => { // noqa
    if (!mapRef.current) return;
    if (polygonRef.current) { mapRef.current.removeLayer(polygonRef.current); polygonRef.current = null; }
    if (!points || points.length < 3) return;
    polygonRef.current = L.polygon(points, { color: "#34d399", weight: 2, fillColor: "#34d399", fillOpacity: 0.12 }).addTo(mapRef.current);
    mapRef.current.fitBounds(polygonRef.current.getBounds(), { padding: [30, 30] });
  } };
}

function Chip({ icon: Icon, label, value, tone = "text-cyan-400" }) {
  return (
    <div className="glass-soft p-3">
      <p className={`text-[10px] font-mono uppercase flex items-center gap-1 ${tone}`}><Icon className="w-3 h-3" /> {label}</p>
      <p className="text-sm font-semibold text-slate-100 mt-0.5">{value}</p>
    </div>
  );
}

export default function Join() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", crop: "", lat: "21.1524", lng: "78.8291", consent: false });
  const [busy, setBusy] = useState(false);
  const [stepIdx, setStepIdx] = useState(-1);
  const [report, setReport] = useState(null);
  const [call, setCall] = useState(null);
  const [status, setStatus] = useState(null);
  const [widget, setWidget] = useState(null);
  const [error, setError] = useState(null);
  const [waBusy, setWaBusy] = useState(false);
  const [waError, setWaError] = useState(null);
  const [locating, setLocating] = useState(false);
  const mapContainer = useRef(null);
  const markerRef = useRef(null);
  const polygonRef = useRef(null);
  const reportRef = useRef(null);
  const { drawPolygon } = useMap(mapContainer, markerRef, polygonRef, (lat, lng) => setForm((f) => ({ ...f, lat: lat.toFixed(6), lng: lng.toFixed(6) })));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const lat = parseFloat(form.lat), lng = parseFloat(form.lng);
    if (markerRef.current && !Number.isNaN(lat) && !Number.isNaN(lng)) markerRef.current.setLatLng([lat, lng]);
  }, [form.lat, form.lng]);

  useEffect(() => {
    if (!busy) return;
    setStepIdx(0);
    const t = setInterval(() => setStepIdx((i) => Math.min(i + 1, PIPELINE.length - 1)), 4200);
    return () => clearInterval(t);
  }, [busy]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return setError("Location is not available in this browser — enter the coordinates manually.");
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setForm((f) => ({ ...f, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }));
        setLocating(false);
      },
      () => {
        setError("Could not read your location — enter the coordinates manually.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setReport(null);
    const lat = parseFloat(form.lat), lng = parseFloat(form.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return setError("Enter valid latitude (−90…90) and longitude (−180…180).");
    const phone = form.phone.replace(/\D/g, "");
    if (phone.length < 10) return setError("Enter a valid 10-digit mobile number.");
    if (!form.consent) return setError("Please allow the call + WhatsApp confirmation to continue.");
    setBusy(true);
    try {
      const { data } = await api.post("/farm/analyze", { lat, lng, name: form.name || null, phone: `+91 ${phone.slice(-10)}`, crop_hint: form.crop || null }, { timeout: 300000 });
      setReport(data);
      setStepIdx(PIPELINE.length);
      const poly = data?.parcel?.polygon;
      if (poly?.length) drawPolygon(poly);
      setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    } catch (err) {
      setError(err?.response?.data?.detail || "Analysis failed — please retry in a minute.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!report?.id || !report?.farmer?.phone) return;
    let alive = true;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get(`/farm/reports/${report.id}/call`, { timeout: 15000 });
        if (alive) setStatus(data);
      } catch (e) { /* keep polling */ }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [report?.id, report?.farmer?.phone]);

  const refreshStatus = async () => {
    try { const { data } = await api.get(`/farm/reports/${report.id}/call`, { timeout: 15000 }); setStatus(data); } catch (e) {}
  };

  const simulateCall = async () => {
    setCall({ status: "initiated" });
    try { await api.post(`/farm/reports/${report.id}/simulate-call`, {}, { timeout: 60000 }); await refreshStatus(); } catch (e) { setCall({ status: "error", message: e.response?.data?.detail || "Simulation failed" }); }
  };

  const simulateYes = async () => {
    try { await api.post(`/farm/reports/${report.id}/simulate-whatsapp`, { text: "YES" }, { timeout: 60000 }); await refreshStatus(); } catch (e) { setCall({ status: "error", message: e.response?.data?.detail || "Simulation failed" }); }
  };

  const loadWidget = async () => {
    setWidget({ status: "loading" });
    try {
      const { data } = await api.get(`/farm/reports/${report.id}/voice-widget`, { timeout: 90000 });
      setWidget({ status: data.signed_url ? "ready" : "unavailable", data });
    } catch (e) {
      setWidget({ status: "error", message: e.response?.data?.detail || "Could not load the voice agent" });
    }
  };

  const reset = () => { setReport(null); setStepIdx(-1); setError(null); setCall(null); setStatus(null); setWaBusy(false); setWaError(null); };

  const pollCall = async (rid) => {
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 6000));
      try {
        const { data } = await api.get(`/farm/reports/${rid}/call`, { timeout: 15000 });
        if (data.call_status === "completed") return setCall({ status: "completed", summary: data.call_summary, transcript: data.call_transcript || [] });
        if (data.call_status === "failed") return setCall({ status: "error", message: "The call could not connect — please try again." });
      } catch (e) { /* keep polling */ }
    }
    setCall({ status: "initiated", slow: true });
  };

  const triggerCall = async () => {
    setCall({ status: "busy" });
    try {
      const { data } = await api.post(`/farm/reports/${report.id}/call`, {}, { timeout: 30000 });
      setCall({ status: "initiated", call: data.call });
      pollCall(report.id);
    } catch (e) {
      setCall({ status: "error", message: e.response?.data?.detail || "Could not start the call." });
    }
  };

  const resendWhatsapp = async () => {
    setWaBusy(true); setWaError(null);
    try {
      await api.post(`/farm/reports/${report.id}/whatsapp`, {}, { timeout: 60000 });
    } catch (e) {
      setWaError(e.response?.data?.detail || "Could not send the WhatsApp message.");
    } finally {
      setWaBusy(false);
      await refreshStatus();
    }
  };

  const inf = report?.inference;
  const lang = status?.call_language
    ? `${status.call_language}${status.call_language_fallback ? " (free-plan fallback)" : ""}`
    : report ? STATE_LANGUAGE[report.location?.state] || "the local language" : null;
  const transcriptMsgs = call?.transcript?.length ? call.transcript : status?.call_transcript;
  const callSummary = call?.summary || status?.call_summary;

  return (
    <div className="min-h-screen bg-[#080C0A] text-slate-200 grain">
      <style>{`
        .pin-outer{width:26px;height:26px;border-radius:9999px;background:rgba(52,211,153,.25);display:flex;align-items:center;justify-content:center}
        .pin-core{width:12px;height:12px;border-radius:9999px;background:#34d399;box-shadow:0 0 12px rgba(52,211,153,.9)}
        .leaflet-container{background:#0a0f0c}
      `}</style>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#080C0A]/70 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Brand size="lg" to="/" />
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link to="/" className="text-sm text-slate-400 hover:text-slate-100 hidden sm:block">For distributors</Link>
            {user ? <Link to="/dashboard" className="btn-ghost btn-sm">Dashboard</Link> : <Link to="/" className="btn-ghost btn-sm">Distributor sign in</Link>}
          </div>
        </div>
      </header>

      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-10">
        <div className="absolute -top-32 -left-40 w-[520px] h-[520px] rounded-full bg-emerald-600/10 blur-[120px] pointer-events-none" />
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="overline-label mb-4">For farmers · free farm report</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-50 max-w-3xl leading-[1.08]">
          Drop your farm pin. <span className="text-emerald-400">See what the satellite says</span> about your crop — then confirm it with one call.
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="text-slate-400 mt-5 max-w-2xl">
          Fields of the World gives us your parcel boundary, Sentinel-2 gives us the crop condition, soil + weather give us the risk. You get the report in your dialect — and the products your crop needs now, from the nearest retailer with stock.
        </motion.p>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 grid lg:grid-cols-12 gap-8">
        {/* form */}
        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} onSubmit={submit} className="lg:col-span-5 glass p-6 space-y-5" data-testid="join-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="field-label">Your name (optional)</label><input className="field" placeholder="Ramesh Patil" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><label className="field-label">Mobile number *</label><input className="field" placeholder="+91 90000 00011" value={form.phone} onChange={(e) => set("phone", e.target.value)} inputMode="tel" /></div>
          </div>
          <div>
            <label className="field-label">Crop you are growing (optional — helps the first guess)</label>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => set("crop", "")} className={`pill-muted ${!form.crop ? "ring-1 ring-emerald-400/60 text-slate-100" : ""}`}>Auto-detect</button>
              {CROPS.map((c) => (
                <button key={c} type="button" onClick={() => set("crop", c)} className={`pill-muted capitalize ${form.crop === c ? "ring-1 ring-emerald-400/60 text-slate-100" : ""}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="field-label">Latitude *</label><input className="field font-mono" value={form.lat} onChange={(e) => set("lat", e.target.value)} /></div>
            <div><label className="field-label">Longitude *</label><input className="field font-mono" value={form.lng} onChange={(e) => set("lng", e.target.value)} /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="field-label mb-0">Tap the map to place your farm pin</span>
              <button type="button" onClick={useMyLocation} className="text-[11px] font-mono text-emerald-300 hover:text-emerald-200 flex items-center gap-1"><Locate className="w-3 h-3" /> Use my location</button>
            </div>
            <div className="relative h-[260px] rounded-xl overflow-hidden border border-white/10">
              <div ref={mapContainer} className="h-full w-full" data-testid="join-map" />
              {(locating || busy) && (
                <div className="absolute inset-0 bg-[#080C0A]/72 backdrop-blur-[2px] flex items-center justify-center" data-testid="join-earth-overlay">
                  <EarthGlobe compact caption={locating ? "Reading your coordinates…" : "Revolving over your farm pin…"} />
                </div>
              )}
            </div>
          </div>
          <label className="flex items-start gap-3 text-xs text-slate-400 cursor-pointer">
            <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 accent-emerald-500" />
            I agree to receive a confirmation call and WhatsApp message about this farm report from AgriVoice partner distributors.
          </label>
          {error && <p className="text-xs text-rose-300 bg-rose-950/40 border border-rose-500/30 rounded-lg px-3 py-2">{error}</p>}
          <button type="submit" disabled={busy || locating} className="btn-primary w-full" data-testid="join-submit">
            {busy ? <>Analyzing your farm…</> : locating ? <>Locating farm…</> : <>Analyze my farm <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-[11px] text-slate-500 font-mono text-center">Signed satellite facts · no card required</p>
        </motion.form>

        {/* pipeline / report */}
        <div className="lg:col-span-7 space-y-6">
          {(busy || (stepIdx >= 0 && !report)) && (
            <div className="glass p-6">
              <p className="overline-label mb-5">Farm analysis pipeline</p>
              <div className="flex justify-center mb-6">
                <EarthGlobe size={196} caption={PIPELINE[Math.max(stepIdx, 0)]?.label || "Reading the field from orbit"} />
              </div>
              <div className="space-y-3">
                {PIPELINE.map((s, i) => {
                  const Icon = s.icon;
                  const done = report ? true : i < stepIdx;
                  const active = !report && i === stepIdx;
                  return (
                    <div key={s.key} className={`flex items-center gap-3 text-sm ${done ? "text-emerald-300" : active ? "text-slate-100" : "text-slate-500"}`}>
                      <span className={`w-7 h-7 rounded-full border flex items-center justify-center ${done ? "border-emerald-500/50 bg-emerald-500/10" : active ? "border-emerald-400/60" : "border-white/10"}`}>
                        {done ? <Check className="w-3.5 h-3.5" /> : active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                      </span>
                      <span>{s.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!busy && !report && stepIdx < 0 && (
            <div className="glass p-6 h-full flex flex-col justify-center">
              <p className="overline-label mb-3">What you'll get</p>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex gap-3"><Satellite className="w-4 h-4 text-cyan-400 mt-0.5" /> Sentinel-2 image + NDVI/NDMI/NDRE for your exact field (signed, verifiable)</li>
                <li className="flex gap-3"><MapPin className="w-4 h-4 text-emerald-400 mt-0.5" /> Your field boundary + acreage from Fields of the World</li>
                <li className="flex gap-3"><SproutIcon className="w-4 h-4 text-emerald-400 mt-0.5" /> Crop & stage guess with the 2–3 questions we'll confirm on a call</li>
                <li className="flex gap-3"><CloudSun className="w-4 h-4 text-amber-400 mt-0.5" /> This week's disease/irrigation risk from live weather</li>
                <li className="flex gap-3"><ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5" /> The right products for your crop stage + nearest retailer stock</li>
              </ul>
            </div>
          )}

          {report && (
            <motion.div ref={reportRef} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="farm-report">
              <div className="glass p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="overline-label mb-1">Farm report · {report.id}</p>
                    <h2 className="font-display text-2xl font-bold text-slate-50">{[report.location.village, report.location.district, report.location.state].filter(Boolean).join(", ") || "Your farm"}</h2>
                    <p className="text-[11px] font-mono text-slate-500 mt-1">emem cell {report.cell64}{report.parcel?.area_acres ? ` · ${report.parcel.area_acres} acres` : ""}</p>
                  </div>
                  <button onClick={reset} className="btn-ghost btn-sm"><RefreshCw className="w-3.5 h-3.5" /> New analysis</button>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 mt-5">
                  <Chip icon={Satellite} label="NDVI" value={`${report.satellite.ndvi} · ${inf?.health || "—"}`} />
                  <Chip icon={CloudSun} label="Weather" value={`${report.weather.temp ?? "—"}°C · ${report.weather.humidity ?? "—"}% RH`} tone="text-amber-400" />
                  <Chip icon={SproutIcon} label="Crop · stage" value={`${inf?.crop ? inf.crop[0].toUpperCase() + inf.crop.slice(1) : "—"} · ${inf?.stage || "—"}`} tone="text-emerald-400" />
                </div>
              </div>

              <div className="glass p-6">
                <p className="overline-label mb-4">Sentinel-2 field view</p>
                <div className="grid sm:grid-cols-5 gap-4 items-start">
                  <div className="sm:col-span-3 relative rounded-xl overflow-hidden border border-white/10 bg-black/40">
                    {report.satellite.scene_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${report.satellite.scene_url}`} alt="Sentinel-2 field" className="w-full aspect-square object-cover" /> : <div className="aspect-square flex items-center justify-center text-slate-500 text-sm">Scene unavailable</div>}
                    <span className="absolute bottom-2 left-2 pill">Sentinel-2 L2A</span>
                  </div>
                  <div className="sm:col-span-2 space-y-3">
                    {[["NDVI", report.satellite.ndvi, "canopy greenness"], ["NDMI", report.satellite.ndmi, "canopy moisture"], ["NDRE", report.satellite.ndre, "chlorophyll / nitrogen"]].map(([k, v, d]) => (
                      <div key={k} className="glass-soft p-3">
                        <div className="flex items-center justify-between"><span className="font-mono text-[11px] text-cyan-300">{k}</span><span className="font-semibold text-slate-100">{v}</span></div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{d}</p>
                      </div>
                    ))}
                    <p className="text-[11px] text-slate-500">NDVI history points: {report.satellite.history?.length || 0}{report.satellite.history?.length ? "" : " · first pass at this field, building history"}</p>
                  </div>
                </div>
              </div>

              <div className="glass p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="overline-label">What the satellite suggests</p>
                  <span className="pill-muted">{inf?.source === "ai" ? "AI inference" : "rule-based inference"}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="glass-soft p-4">
                    <p className="text-xs text-slate-400">Likely crop & stage</p>
                    <p className="font-display text-xl font-bold text-slate-50 mt-1 capitalize">{inf?.crop} · {inf?.stage}</p>
                    <div className="flex gap-4 mt-3 text-[11px] font-mono text-slate-400">
                      <span>crop confidence {inf?.crop_confidence}%</span><span>stage {inf?.stage_confidence}%</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-3">{inf?.ndvi_read}</p>
                  </div>
                  <div className="glass-soft p-4">
                    <p className="text-xs text-slate-400">This week's watch-outs</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-slate-200">
                      {(inf?.stress_notes || []).map((s, i) => <li key={i} className="flex gap-2"><span className="text-amber-400">•</span>{s}</li>)}
                      {(report.pressure || []).length > 0 && <li className="flex gap-2"><span className="text-rose-400">•</span>Pressure: {report.pressure.join(", ")}</li>}
                    </ul>
                    <p className="text-xs text-emerald-300 mt-3">{inf?.irrigation_advice}</p>
                  </div>
                </div>
                <div className="mt-4 glass-soft p-4">
                  <p className="text-xs text-slate-400 mb-2">We'll confirm these on the call{lang ? ` (in ${lang})` : ""}:</p>
                  <ol className="space-y-1.5 text-sm text-slate-200 list-decimal list-inside">
                    {(inf?.questions || []).map((q, i) => <li key={i}>{q}</li>)}
                  </ol>
                </div>
              </div>

              {report.recommendations?.length > 0 && (
                <div className="glass p-6">
                  <p className="overline-label mb-4">What your crop needs now</p>
                  <div className="space-y-2.5">
                    {report.recommendations.map((r) => (
                      <div key={r.sku} className="glass-soft p-3.5 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-100">{r.name} <span className="text-slate-500 font-normal">· {r.composition}</span></p>
                          <p className="text-[11px] text-slate-400 mt-0.5">{r.purpose} · {r.pack}</p>
                        </div>
                        <span className="font-mono text-sm text-amber-300">₹{r.price}</span>
                      </div>
                    ))}
                  </div>
                  {report.catalogue_gaps?.length > 0 && <p className="text-[11px] text-slate-500 mt-3">Composition gaps in the local catalogue: {report.catalogue_gaps.map((g) => g.required_composition).join(", ")}</p>}
                </div>
              )}

              <div className="glass p-6 border-emerald-500/20">
                <div className="flex items-start gap-4">
                  <div className="relative w-11 h-11 shrink-0"><span className="absolute inset-0 rounded-full bg-emerald-500/40 pulse-ring" /><div className="absolute inset-0 rounded-full bg-emerald-500 flex items-center justify-center"><Phone className="w-4 h-4 text-[#06110A]" /></div></div>
                  <div className="flex-1">
                    <p className="font-display font-bold text-slate-50">Next: your confirmation call{report.farmer?.phone ? ` on ${report.farmer.phone}` : ""}</p>
                    <p className="text-sm text-slate-400 mt-1">Our krishi advisor{lang ? ` calls you in ${lang}` : ""} to confirm <span className="capitalize text-slate-200">{inf?.crop} · {inf?.stage}</span>{report.farmer?.phone ? "" : " (add your number to book it)"}. Confirmed details are then sent on WhatsApp with the product list and the nearest retailer that has stock.</p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <span className="pill">Call + WhatsApp</span><span className="pill-gold">{lang || "Local dialect"}</span><span className="pill-muted">ElevenLabs voice</span>
                    </div>
                    {report.farmer?.phone && (
                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <button onClick={triggerCall} disabled={call?.status === "busy" || call?.status === "initiated"} className="btn-primary btn-sm" data-testid="call-me-btn">
                          {call?.status === "busy" ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Connecting…</> : call?.status === "initiated" ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Calling {report.farmer.phone}…</> : <><Phone className="w-3.5 h-3.5" /> Call me now to confirm</>}
                        </button>
                        {call?.status === "initiated" && !call.slow && <span className="text-xs text-slate-400">Your phone is ringing — confirm the crop and stage on the call.</span>}
                        {call?.status === "initiated" && call.slow && <span className="text-xs text-slate-400">Call placed. The transcript appears here once it ends.</span>}
                        {call?.status === "error" && <span className="text-xs text-rose-300 max-w-md">{call.message}</span>}
                      </div>
                    )}
                    {(call?.status === "completed" || (status?.call_status === "completed" && transcriptMsgs?.length)) && (
                      <div className="mt-4 glass-soft p-4">
                        <p className="text-[11px] font-mono uppercase tracking-wider text-emerald-300 mb-2">Call confirmed · transcript{status?.call_language ? ` · ${status.call_language}` : ""}</p>
                        {status?.call_audio_url && (
                          <audio controls preload="none" className="w-full mb-3 h-9" src={`${process.env.REACT_APP_BACKEND_URL}${status.call_audio_url}`} data-testid="call-recording">
                            Your browser does not support audio playback.
                          </audio>
                        )}
                        {callSummary && <p className="text-sm text-slate-300 mb-3">{callSummary}</p>}
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                          {(transcriptMsgs || []).map((t, i) => <div key={i} className={t.speaker === "agent" ? "wa-bubble-agent" : "wa-bubble-contact"}>{t.text}</div>)}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <button onClick={resendWhatsapp} disabled={waBusy} className="btn-ghost btn-sm" data-testid="resend-whatsapp-btn">
                            {waBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</> : <><MessageCircle className="w-3.5 h-3.5" /> {status?.whatsapp_status === "failed" ? "Retry WhatsApp" : "Send WhatsApp again"}</>}
                          </button>
                          {status?.whatsapp_status && <span className="text-[11px] font-mono text-slate-500">whatsapp · {status.whatsapp_status}</span>}
                          {waError && <span className="text-xs text-rose-300">{waError}</span>}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 glass-soft p-4 border border-emerald-500/20">
                      <p className="text-[11px] font-mono uppercase tracking-wider text-emerald-300 mb-2">Talk to your advisor — live in the browser · free</p>
                      {!widget && (
                        <button onClick={loadWidget} className="btn-primary btn-sm" data-testid="voice-widget-btn"><Mic className="w-3.5 h-3.5" /> Start live voice session</button>
                      )}
                      {widget?.status === "loading" && (
                        <div className="flex items-center gap-4 py-2">
                          <EarthGlobe compact caption="Warming the voice agent over your field…" />
                        </div>
                      )}
                      {widget?.status === "unavailable" && <p className="text-xs text-amber-300">{(widget.data && (widget.data.detail || widget.data.error)) || "Voice not configured yet."}</p>}
                      {widget?.status === "error" && <p className="text-xs text-rose-300">{widget.message}</p>}
                      {widget?.status === "ready" && (
                        <>
                          <p className="text-xs text-slate-400 mb-2">The agent{report.inference?.crop ? ` knows this farm: ${report.inference.crop} · ${report.inference.stage}` : ""}{widget.data.language ? ` · speaks ${widget.data.language}${widget.data.language_fallback ? " (plan fallback)" : ""}` : ""}. Press the green bubble bottom-right, allow the microphone and speak — it will confirm your crop, suggest products and the nearest retailer.</p>
                          <elevenlabs-convai agent-id={widget.data.agent_id} signed-url={widget.data.signed_url} dynamic-variables={JSON.stringify(widget.data.dynamic_variables)} />
                        </>
                      )}
                    </div>
                    {status?.dev_tools && (
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        <span className="pill-muted">dev tools</span>
                        <button onClick={simulateCall} className="btn-ghost btn-sm" data-testid="simulate-call-btn">Simulate call</button>
                        {!status?.order && <button onClick={simulateYes} className="btn-gold btn-sm" data-testid="simulate-yes-btn">Simulate farmer reply: YES</button>}
                      </div>
                    )}
                    {(status?.whatsapp?.length || 0) > 0 && (
                      <div className="mt-4 glass-soft p-4">
                        <p className="text-[11px] font-mono uppercase tracking-wider text-emerald-300 mb-3">WhatsApp thread{status.whatsapp_status ? ` · ${status.whatsapp_status}` : ""}</p>
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                          {(status.whatsapp || []).map((m, i) => (
                            <div key={i} className={m.from === "agent" ? "wa-bubble-agent" : "wa-bubble-contact"}>
                              {m.text}
                              {m.error ? (
                                <span className="block text-[10px] font-mono text-rose-300 mt-1">
                                  failed: {m.error}{m.error_hint ? ` — ${m.error_hint}` : ""}
                                </span>
                              ) : m.simulated ? (
                                <span className="block text-[10px] font-mono text-slate-500 mt-1">not sent — WhatsApp provider is not configured</span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {status?.order && (
                      <div className="mt-4 glass-soft p-4 border border-emerald-500/30">
                        <p className="text-[11px] font-mono uppercase tracking-wider text-amber-300 mb-1">PO raised · {status.order.id}</p>
                        <p className="text-sm text-slate-200">₹{status.order.value} · {(status.order.items || []).length} items{status.order.routed_retailer ? ` · routed to ${status.order.routed_retailer.name} (${status.order.routed_retailer.business_model})` : ""}</p>
                        <p className="text-[11px] text-slate-500 mt-1">Now visible in the distributor dashboard under Orders → convert to SO.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="glass p-6">
                <p className="overline-label mb-3">Provenance · {report.receipts?.length || 0} signed facts</p>
                <div className="space-y-2">
                  {(report.receipts || []).slice(0, 3).map((cid) => (
                    <a key={cid} href={`https://emem.dev/verify/${cid}`} target="_blank" rel="noreferrer" className="flex items-center justify-between text-[11px] font-mono text-slate-400 hover:text-emerald-300 glass-soft px-3 py-2">
                      <span className="truncate max-w-[70%]">{cid}</span><span className="flex items-center gap-1">verify <ExternalLink className="w-3 h-3" /></span>
                    </a>
                  ))}
                </div>
                {report.parcel?.attribution && <p className="text-[10px] text-slate-600 mt-3">{report.parcel.attribution} · weather by Open-Meteo · analysis in {Math.round(report.pipeline_ms / 1000)}s</p>}
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
